import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Trash2, Save, Plus, FileText, MessageSquare, ClipboardList,
  CheckCircle2, Circle, UploadCloud, Users, Activity, ArrowLeft, AlertTriangle,
} from "lucide-react";

const STATUS_OPTIONS = ["draft", "active", "on_hold", "completed", "cancelled"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];
const SERVICE_OPTIONS = [
  "custom_design", "plan_sale", "modification", "consultation",
  "structural", "3d_rendering", "interior_design", "other",
];
const TEAM_ROLE_OPTIONS = [
  "architect", "team_lead", "draftsman", "renderer",
  "structural_engineer", "interior_designer", "sales_rep",
];

const AdminProjectDetail = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roster, setRoster] = useState([]);
  const [tab, setTab] = useState("overview");

  // Sub-resource state
  const [tasks, setTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newComment, setNewComment] = useState("");
  const [newTeamUserId, setNewTeamUserId] = useState("");
  const [newTeamRole, setNewTeamRole] = useState("architect");

  const loadProject = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/projects/${projectId}`);
      setProj(data);
    } catch (err) {
      toast.error("Could not load project", {
        description: err?.response?.data?.detail || "It may have been deleted.",
      });
      navigate("/admin/projects");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  const loadRoster = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/projects/_roster/assignable-users");
      setRoster(data.items || []);
    } catch (err) { console.warn("[projects] roster load failed", err); }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/projects/${projectId}/tasks`);
      setTasks(data.items || []);
    } catch (err) { console.warn("[projects] tasks load failed", err); }
  }, [projectId]);

  const loadFiles = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/projects/${projectId}/files`);
      setFiles(data.items || []);
    } catch (err) { console.warn("[projects] files load failed", err); }
  }, [projectId]);

  const loadComments = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/projects/${projectId}/comments`);
      setComments(data.items || []);
    } catch (err) { console.warn("[projects] comments load failed", err); }
  }, [projectId]);

  const loadActivity = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/projects/${projectId}/activity`);
      setActivity(data.items || []);
    } catch (err) { console.warn("[projects] activity load failed", err); }
  }, [projectId]);

  useEffect(() => {
    loadProject();
    loadRoster();
  }, [loadProject, loadRoster]);

  useEffect(() => {
    if (!proj) return;
    if (tab === "tasks") loadTasks();
    if (tab === "files") loadFiles();
    if (tab === "comments") loadComments();
    if (tab === "timeline" || tab === "activity") loadActivity();
  }, [tab, proj, loadTasks, loadFiles, loadComments, loadActivity]);

  const saveField = async (patch, activityLabel = "Updated") => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/projects/${projectId}`, patch);
      setProj(data);
      toast.success(activityLabel, { duration: 2500 });
      loadActivity();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const addTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      await api.post(`/admin/projects/${projectId}/tasks`, { title: newTaskTitle, status: "open" });
      setNewTaskTitle("");
      toast.success("Task added");
      loadTasks();
    } catch (err) {
      toast.error("Could not add task", { description: err?.response?.data?.detail || "" });
    }
  };

  const toggleTask = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    try {
      await api.patch(`/admin/projects/${projectId}/tasks/${t.id}`, { status: next });
      loadTasks();
    } catch (err) { toast.error("Could not update task"); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/admin/projects/${projectId}/comments`, { body: newComment });
      setNewComment("");
      toast.success("Comment posted");
      loadComments();
    } catch (err) {
      toast.error("Could not post comment", { description: err?.response?.data?.detail || "" });
    }
  };

  const addTeamMember = async () => {
    if (!newTeamUserId) return;
    const current = proj.team_members || [];
    if (current.some((m) => m.user_id === newTeamUserId)) {
      toast.info("Already on the team");
      return;
    }
    const nextTeam = [...current, { user_id: newTeamUserId, role_in_project: newTeamRole }];
    try {
      await api.post(`/admin/projects/${projectId}/assign`, { team_members: nextTeam, notify: true });
      setNewTeamUserId("");
      setNewTeamRole("architect");
      toast.success("Team member added — email notification sent");
      loadProject();
    } catch (err) {
      toast.error("Could not add team member", { description: err?.response?.data?.detail || "" });
    }
  };

  const removeTeamMember = async (uid) => {
    const nextTeam = (proj.team_members || []).filter((m) => m.user_id !== uid);
    try {
      await api.post(`/admin/projects/${projectId}/assign`, { team_members: nextTeam, notify: false });
      toast.success("Removed from team");
      loadProject();
    } catch (err) { toast.error("Could not remove"); }
  };

  const changeArchitect = async (uid) => {
    try {
      await api.post(`/admin/projects/${projectId}/assign`, {
        assigned_architect_id: uid === "__none__" ? null : uid,
        notify: true,
      });
      toast.success("Architect updated");
      loadProject();
    } catch (err) {
      toast.error("Could not assign", { description: err?.response?.data?.detail || "" });
    }
  };

  const deleteProject = async () => {
    if (!window.confirm(`Really delete ${proj.project_number}? This is a soft delete (recoverable).`)) return;
    try {
      await api.delete(`/admin/projects/${projectId}`);
      toast.success("Project deleted");
      navigate("/admin/projects");
    } catch (err) { toast.error("Delete failed"); }
  };

  const architectName = useMemo(() => {
    const u = roster.find((r) => r.user_id === proj?.assigned_architect_id);
    return u ? `${u.name || u.email} (${u.role})` : null;
  }, [roster, proj]);

  const userLookup = (uid) => roster.find((r) => r.user_id === uid);

  if (loading || !proj) {
    return <AdminLayout><div className="p-10 text-center text-muted-foreground">Loading project…</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-project-detail">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <button onClick={() => navigate("/admin/projects")} className="text-sm text-muted-foreground hover:underline flex items-center gap-1 mb-2">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Projects
            </button>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="project-title">{proj.title || "(untitled)"}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              <span data-testid="project-number">{proj.project_number}</span>
              <span>·</span>
              <span className="capitalize">{(proj.service_type || "").replace(/_/g, " ")}</span>
              {proj.client_name && (<><span>·</span><span>{proj.client_name}</span></>)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={proj.status} onValueChange={(v) => saveField({ status: v }, `Status → ${v.replace(/_/g," ")}`)}>
              <SelectTrigger data-testid="project-status-select" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={proj.priority} onValueChange={(v) => saveField({ priority: v }, `Priority → ${v}`)}>
              <SelectTrigger data-testid="project-priority-select" className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITY_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="project-delete-btn" variant="ghost" size="icon" onClick={deleteProject} title="Delete project"><Trash2 className="h-4 w-4 text-rose-600" /></Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 md:grid-cols-7 gap-1" data-testid="project-tabs">
            <TabsTrigger data-testid="tab-overview" value="overview">Overview</TabsTrigger>
            <TabsTrigger data-testid="tab-tasks" value="tasks">Tasks {proj._counts?.tasks ? `(${proj._counts.tasks})` : ""}</TabsTrigger>
            <TabsTrigger data-testid="tab-files" value="files">Files {proj._counts?.files ? `(${proj._counts.files})` : ""}</TabsTrigger>
            <TabsTrigger data-testid="tab-comments" value="comments">Comments {proj._counts?.comments ? `(${proj._counts.comments})` : ""}</TabsTrigger>
            <TabsTrigger data-testid="tab-timeline" value="timeline">Timeline</TabsTrigger>
            <TabsTrigger data-testid="tab-team" value="team">Team</TabsTrigger>
            <TabsTrigger data-testid="tab-activity" value="activity">Activity Log</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-4" data-testid="overview-tab-panel">
            <Card>
              <CardHeader><CardTitle className="text-base">Client</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Client name</Label>
                  <Input data-testid="client-name-input" defaultValue={proj.client_name || ""} onBlur={(e) => e.target.value !== proj.client_name && saveField({ client_name: e.target.value })} />
                </div>
                <div>
                  <Label>Client email</Label>
                  <Input data-testid="client-email-input" defaultValue={proj.client_email || ""} onBlur={(e) => e.target.value !== proj.client_email && saveField({ client_email: e.target.value })} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Project details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input data-testid="project-title-input" defaultValue={proj.title || ""} onBlur={(e) => e.target.value !== proj.title && saveField({ title: e.target.value })} />
                </div>
                <div>
                  <Label>Service type</Label>
                  <Select value={proj.service_type} onValueChange={(v) => saveField({ service_type: v })}>
                    <SelectTrigger data-testid="project-service-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start date</Label>
                  <Input data-testid="project-start-date" type="date" defaultValue={proj.start_date || ""} onBlur={(e) => e.target.value !== proj.start_date && saveField({ start_date: e.target.value })} />
                </div>
                <div>
                  <Label>Due date</Label>
                  <Input data-testid="project-due-date" type="date" defaultValue={proj.due_date || ""} onBlur={(e) => e.target.value !== proj.due_date && saveField({ due_date: e.target.value })} />
                </div>
                <div>
                  <Label>Estimated hours</Label>
                  <Input data-testid="project-hours" type="number" defaultValue={proj.estimated_hours ?? ""} onBlur={(e) => Number(e.target.value) !== proj.estimated_hours && saveField({ estimated_hours: Number(e.target.value) || null })} />
                </div>
                <div>
                  <Label>Budget (USD)</Label>
                  <Input data-testid="project-budget" type="number" defaultValue={proj.budget_usd ?? ""} onBlur={(e) => Number(e.target.value) !== proj.budget_usd && saveField({ budget_usd: Number(e.target.value) || null })} />
                </div>
                <div className="md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea data-testid="project-notes" rows={4} defaultValue={proj.notes || ""} onBlur={(e) => e.target.value !== proj.notes && saveField({ notes: e.target.value })} />
                </div>
              </CardContent>
            </Card>

            {proj.source_enquiry_id && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Source</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  Converted from enquiry <span className="font-mono">{proj.source_enquiry_id}</span> ({proj.source_type})
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tasks */}
          <TabsContent value="tasks" data-testid="tasks-tab-panel">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Tasks</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input data-testid="new-task-input" placeholder="Add a task…" value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} />
                  <Button data-testid="add-task-btn" onClick={addTask}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                </div>
                {tasks.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No tasks yet.</div>
                ) : (
                  <ul className="divide-y">
                    {tasks.map((t) => (
                      <li key={t.id} data-testid={`task-${t.id}`} className="py-2 flex items-center gap-3">
                        <button onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`}>
                          {t.status === "done"
                            ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            : <Circle className="h-5 w-5 text-slate-400" />}
                        </button>
                        <span className={`flex-1 ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
                        {t.due_date && <span className="text-xs text-muted-foreground">Due {t.due_date}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Files */}
          <TabsContent value="files" data-testid="files-tab-panel">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><UploadCloud className="h-4 w-4" /> Files</CardTitle></CardHeader>
              <CardContent>
                {files.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">
                    No files yet. File uploads use the same storage as the Media Library.
                    Cloud-storage integration (S3/R2/B2) is on the roadmap — current uploads live on the server disk.
                  </div>
                ) : (
                  <ul className="divide-y">
                    {files.map((f) => (
                      <li key={f.id} data-testid={`file-${f.id}`} className="py-2 flex items-center gap-3">
                        <FileText className="h-4 w-4 text-slate-500" />
                        <a href={f.file_url} target="_blank" rel="noreferrer" className="flex-1 hover:underline">{f.original_name}</a>
                        <span className="text-xs text-muted-foreground capitalize">{f.kind}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comments */}
          <TabsContent value="comments" data-testid="comments-tab-panel">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Internal comments</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4 max-h-96 overflow-y-auto pr-2">
                  {comments.length === 0
                    ? <div className="text-sm text-muted-foreground py-6 text-center">No comments yet. Start the discussion.</div>
                    : comments.map((c) => (
                        <div key={c.id} data-testid={`comment-${c.id}`} className="border rounded-md p-3">
                          <div className="text-xs text-muted-foreground mb-1">
                            <span className="font-medium text-slate-900">{c.author_name || c.author_email}</span> · {c.author_role} · {c.created_at?.slice(0, 16).replace("T", " ")}
                          </div>
                          <div className="whitespace-pre-wrap text-sm">{c.body}</div>
                        </div>
                      ))}
                </div>
                <div className="flex gap-2">
                  <Textarea data-testid="new-comment-input" rows={2} placeholder="Write an internal comment…" value={newComment} onChange={(e) => setNewComment(e.target.value)} />
                  <Button data-testid="post-comment-btn" onClick={addComment}><Save className="h-4 w-4 mr-1" /> Post</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timeline (mirrors activity, chronological) */}
          <TabsContent value="timeline" data-testid="timeline-tab-panel">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" /> Timeline</CardTitle></CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">Timeline is empty.</div>
                ) : (
                  <ol className="relative border-l ml-2 space-y-4">
                    {activity.map((a) => (
                      <li key={a.id} data-testid={`activity-${a.id}`} className="ml-4">
                        <div className="absolute w-2 h-2 bg-slate-400 rounded-full -left-1 mt-2" />
                        <div className="text-xs text-muted-foreground">{a.created_at?.slice(0, 16).replace("T", " ")}</div>
                        <div className="text-sm"><b>{a.actor_name || a.actor_email || "system"}</b> · <span className="text-slate-700">{a.action}</span></div>
                        {a.metadata && Object.keys(a.metadata).length > 0 && (
                          <div className="text-xs text-muted-foreground mt-0.5">{JSON.stringify(a.metadata)}</div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team */}
          <TabsContent value="team" data-testid="team-tab-panel">
            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Lead architect</CardTitle></CardHeader>
                <CardContent className="flex items-center gap-3">
                  <Select value={proj.assigned_architect_id || "__none__"} onValueChange={changeArchitect}>
                    <SelectTrigger data-testid="architect-select" className="max-w-md"><SelectValue placeholder="Assign an architect…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {roster.filter((r) => ["architect", "super_admin", "admin"].includes(r.role)).map((u) => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email} — {u.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {architectName && <span className="text-sm text-emerald-700">Assigned: {architectName}</span>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Team members</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Select value={newTeamUserId} onValueChange={setNewTeamUserId}>
                      <SelectTrigger data-testid="team-user-select" className="flex-1"><SelectValue placeholder="Select user…" /></SelectTrigger>
                      <SelectContent>{roster.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email} — {u.role}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={newTeamRole} onValueChange={setNewTeamRole}>
                      <SelectTrigger data-testid="team-role-select" className="w-52"><SelectValue /></SelectTrigger>
                      <SelectContent>{TEAM_ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button data-testid="add-team-member-btn" onClick={addTeamMember}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                  </div>
                  {(proj.team_members || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">No team members yet.</div>
                  ) : (
                    <ul className="divide-y">
                      {proj.team_members.map((m) => {
                        const u = userLookup(m.user_id);
                        return (
                          <li key={m.user_id} className="py-2 flex items-center gap-3" data-testid={`team-member-${m.user_id}`}>
                            <div className="flex-1">
                              <div className="text-sm font-medium">{u?.name || u?.email || m.user_id}</div>
                              <div className="text-xs text-muted-foreground">{u?.email} · Project role: {m.role_in_project?.replace(/_/g, " ")}</div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => removeTeamMember(m.user_id)} data-testid={`remove-team-${m.user_id}`}>
                              <Trash2 className="h-4 w-4 text-rose-600" />
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Activity Log — same data as Timeline, but as a flat log */}
          <TabsContent value="activity" data-testid="activity-tab-panel">
            <Card>
              <CardHeader><CardTitle className="text-base">Full activity log</CardTitle></CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No events yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
                        <tr>
                          <th className="text-left py-2 pr-3">When</th>
                          <th className="text-left py-2 pr-3">Actor</th>
                          <th className="text-left py-2 pr-3">Event</th>
                          <th className="text-left py-2 pr-3">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activity.map((a) => (
                          <tr key={a.id} className="border-b last:border-b-0">
                            <td className="py-2 pr-3 text-xs">{a.created_at?.slice(0, 19).replace("T", " ")}</td>
                            <td className="py-2 pr-3">{a.actor_name || a.actor_email || "system"}</td>
                            <td className="py-2 pr-3 font-mono text-xs">{a.action}</td>
                            <td className="py-2 pr-3 text-xs text-muted-foreground">{a.metadata ? JSON.stringify(a.metadata) : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminProjectDetail;
